import { repairImageKitUrl } from '@dupe/common/constants'
import {
  SEO_REPORT_CLICK_SOURCE,
  toBuyLinkHref,
} from '~/src/lib/withRedirectClickSource'
import { BadgeCheck, ChevronRight, ImageOff } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import { Skeleton } from '~/src/components/ui/skeleton'
import { useSeoReportAnalytics } from '~/src/components/reports/SeoReportAnalyticsContext'
import { cn } from '~/src/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BuyLink {
  label: string
  url: string
  price?: number | null
  logoUrl?: string | null
  verified?: boolean
}

export interface SeoReportProductCardProps {
  images: string[]
  name: string
  roleLabel?: string | null
  roleDescription?: string | null
  summary?: string | null
  buyLinks: BuyLink[]
  currency?: string | null
  /** Anchor slug for "See details" link (used in TopPicks cards) */
  slug?: string
  /** Visual variant: 'full' for ranked product layout, 'compact' for top picks cards */
  variant?: 'full' | 'compact'
  /** Card index for loading priority (top picks carousel) */
  index?: number
  /** Apply blue glow treatment for the top pick product card */
  isTopPick?: boolean
  /** Show an admin-only processing mask while top product images are checked */
  isImageChecking?: boolean
  /** When set, "See details" and clicking the card body open the drawer */
  onViewProduct?: (slug: string) => void
  analyticsSection?: string
  analyticsPosition?: number
}

// ---------------------------------------------------------------------------
// Role badge colors
// ---------------------------------------------------------------------------

function getRoleBadgeClasses(roleLabel: string): string {
  const lower = roleLabel.toLowerCase()
  // Card 1: "Top pick" / "Best overall" -- filled teal, white text
  if (lower.includes('best overall') || lower.includes('top pick'))
    return 'bg-ds-bg-brand-solid text-white'
  // Card 2: "Safe choice" / "Runner up" -- light blue bg, blue text
  if (lower.includes('runner') || lower.includes('safe choice'))
    return 'bg-[#f0f9ff] text-[#175cd3]' /* color-token-exception: role badge distinct color per CEO requirement; no semantic token for light-blue/dark-blue badge pair */
  // Card 3: "Best value" / "Alternate pick" -- light orange bg, orange text
  if (
    lower.includes('alternate') ||
    lower.includes('best value') ||
    lower.includes('high-end') ||
    lower.includes('budget')
  )
    return 'bg-[#fff4ed] text-[#b93815]' /* color-token-exception: role badge distinct color per CEO requirement; no semantic token for light-orange/dark-orange badge pair */
  // Card 4: "Worth a look" -- filled teal, white text
  if (lower.includes('worth a look') || lower.includes('notable'))
    return 'bg-sky-accent-500 text-white'
  return 'bg-sky-accent-500 text-white'
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function canUseNextImage(url: string): boolean {
  if (url.startsWith('/')) return true
  try {
    const hostname = new URL(url).hostname
    return hostname === 'ik.imagekit.io' || hostname === 'assets.dupe.com'
  } catch {
    return false
  }
}

function formatPrice(
  price: number | null | undefined,
  currency?: string | null
): string {
  if (price == null) return ''
  const roundedPrice = Math.round(price)
  const currencyCode = currency ?? 'USD'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedPrice)
  } catch {
    return `$${roundedPrice}`
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ImagePlaceholder({ size = 'size-10' }: { size?: string }) {
  return (
    <div
      className='absolute inset-0 flex h-full w-full items-center justify-center bg-ds-bg-secondary'
      role='img'
      aria-label='Image unavailable'
    >
      <ImageOff
        className={cn(size, 'text-ds-fg-quaternary')}
        strokeWidth={1.5}
      />
    </div>
  )
}

function ImageCheckSkeletonOverlay() {
  return (
    <div
      className='absolute inset-0 z-10 flex items-center justify-center bg-white/85 backdrop-blur-[1px]'
      aria-label='Image check in progress'
    >
      <div className='flex w-32 flex-col items-center gap-2'>
        <Skeleton className='h-4 w-full rounded-full' />
        <Skeleton className='h-4 w-20 rounded-full' />
      </div>
    </div>
  )
}

function ProductImage({
  src,
  alt,
  fill,
  className,
  sizes,
  priority,
  width,
  height,
}: {
  src: string
  alt: string
  fill?: boolean
  className?: string
  sizes?: string
  priority?: boolean
  width?: number
  height?: number
}) {
  const [hasError, setHasError] = useState(false)
  const imgSrc = repairImageKitUrl(src)

  if (hasError) return <ImagePlaceholder />

  if (canUseNextImage(imgSrc)) {
    return (
      <Image
        src={imgSrc}
        alt={alt}
        fill={fill}
        width={!fill ? width : undefined}
        height={!fill ? height : undefined}
        sizes={sizes}
        // Eager (not `priority`): priority emits a preload link that competes
        // with the report hero — the actual LCP element — for bandwidth.
        // fetchPriority=low because React 19 SSR auto-preloads any non-lazy
        // img unless it's marked low — those preloads compete with the hero
        // the same way `priority` ones would.
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'low' : undefined}
        className={className}
        onError={() => setHasError(true)}
      />
    )
  }
  return (
    <img
      src={imgSrc}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'low' : undefined}
      className={cn(fill && 'absolute inset-0 h-full w-full', className)}
      onError={() => setHasError(true)}
    />
  )
}

function RetailerRow({
  label,
  url,
  price,
  currency,
  logoUrl,
  verified,
  onClick,
}: {
  label: string
  url: string
  price?: number | null
  currency?: string | null
  logoUrl?: string | null
  verified?: boolean
  onClick?: () => void
}) {
  const [logoError, setLogoError] = useState(false)

  const showLogo = logoUrl && !logoError

  return (
    <a
      href={toBuyLinkHref(url, SEO_REPORT_CLICK_SOURCE)}
      target='_blank'
      rel='sponsored nofollow noopener noreferrer'
      className='flex items-center justify-between border-b border-black/[0.08] py-2 transition-opacity hover:opacity-80'
      onClick={onClick}
    >
      <div className='flex items-center gap-2'>
        {showLogo ? (
          <span className='flex h-7 w-7 items-center justify-center overflow-hidden rounded border border-border bg-white'>
            <img
              src={logoUrl}
              alt={`${label} logo`}
              loading='lazy'
              className='h-full w-full object-cover'
              onError={() => setLogoError(true)}
            />
          </span>
        ) : (
          <span className='flex h-7 w-7 items-center justify-center rounded border border-border bg-white text-xs font-bold text-foreground-secondary'>
            {label.charAt(0).toUpperCase()}
          </span>
        )}
        <span className='flex items-center gap-1'>
          <span className='text-sm font-semibold leading-5 text-info-700'>
            {label}
          </span>
          {verified && (
            <BadgeCheck
              className='h-3.5 w-3.5 shrink-0 text-info-700'
              aria-label='Verified seller'
            />
          )}
        </span>
      </div>
      <div className='flex items-center gap-0.5'>
        {price != null && (
          <span className='text-sm font-semibold leading-5 text-info-700'>
            {formatPrice(price, currency)}
          </span>
        )}
        <ChevronRight className='size-4 text-info-700' />
      </div>
    </a>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SeoReportProductCard({
  images,
  name,
  roleLabel,
  roleDescription,
  summary,
  buyLinks,
  currency,
  slug,
  variant = 'full',
  index,
  isTopPick,
  isImageChecking = false,
  onViewProduct,
  analyticsSection,
  analyticsPosition,
}: SeoReportProductCardProps) {
  const reportAnalytics = useSeoReportAnalytics()

  if (variant === 'compact') {
    return (
      <CompactProductCard
        image={images[0] ?? null}
        name={name}
        roleLabel={roleLabel}
        roleDescription={roleDescription}
        summary={summary}
        buyLinks={buyLinks}
        currency={currency}
        slug={slug}
        index={index}
        isImageChecking={isImageChecking}
        onViewProduct={onViewProduct}
        reportAnalytics={reportAnalytics}
        analyticsSection={analyticsSection}
        analyticsPosition={analyticsPosition}
      />
    )
  }

  const heroImage = images[0] ?? null
  const isClickable = Boolean(slug && onViewProduct)
  // Guard at the card level instead of stopPropagation in every interactive
  // child: if the click landed on a nested <a> or <button>, let that element
  // handle it on its own and don't open the drawer.
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!slug || !onViewProduct) return
    if ((e.target as HTMLElement).closest('a, button')) return
    onViewProduct(slug)
  }
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (slug && onViewProduct) onViewProduct(slug)
    }
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-3xl border-[0.5px] border-border bg-white p-4 shadow-[0px_4px_6px_0px_rgba(10,13,18,0.05),0px_2px_4px_0px_rgba(10,13,18,0.04)] md:flex-row' /* color-token-exception: multi-layer box-shadow using ink-equivalent opacity */,
        isTopPick &&
          'border border-ds-border-brand-alt shadow-ds-glow-brand-light',
        isClickable && 'cursor-pointer'
      )}
      onClick={isClickable ? handleCardClick : undefined}
      onKeyDown={isClickable ? handleCardKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      // No aria-label: as a role=button the card names itself from its
      // visible content — an override label that doesn't contain that text
      // fails WCAG 2.5.3 (label-content-name-mismatch).
    >
      {/* Left: product image. On mobile the role badge overlays the
          top-left corner; on desktop it lives in the details column. */}
      <div className='w-full shrink-0 md:w-[314px]'>
        <div className='relative aspect-square w-full overflow-hidden rounded-xl border-[0.5px] border-border'>
          {isImageChecking && <ImageCheckSkeletonOverlay />}
          {heroImage ? (
            <ProductImage
              src={heroImage}
              alt={name}
              fill
              sizes='(max-width: 768px) 100vw, 350px'
              className='object-contain'
            />
          ) : (
            <ImagePlaceholder size='size-12' />
          )}
          {roleLabel && (
            <span
              className={cn(
                'absolute left-3 top-3 inline-block rounded-full px-3 py-0.5 text-xs font-medium leading-5 md:hidden',
                getRoleBadgeClasses(roleLabel)
              )}
            >
              {roleLabel === 'Best Overall' ? 'Top Pick' : roleLabel}
            </span>
          )}
        </div>
      </div>

      {/* Right: details */}
      <div className='flex min-w-0 flex-1 flex-col gap-3'>
        <div className='flex flex-col gap-2'>
          {/* Role badge -- desktop only (mobile badge rendered above the image) */}
          {roleLabel && (
            <div className='hidden md:block'>
              <span
                className={cn(
                  'inline-block rounded-full px-3 py-0.5 text-xs font-medium leading-5 md:py-1 md:text-sm',
                  getRoleBadgeClasses(roleLabel)
                )}
              >
                {roleLabel === 'Best Overall' ? 'Top Pick' : roleLabel}
              </span>
            </div>
          )}

          {/* Role description title */}
          {roleDescription && (
            <p className='line-clamp-2 text-balance font-display text-2xl font-semibold leading-[30px] text-foreground'>
              {roleDescription}
            </p>
          )}

          {/* Product model name */}
          <p className='text-balance text-sm font-medium leading-6 text-foreground-secondary md:text-base'>
            {name}
          </p>
        </div>

        {/* Retailer buy links. The "See details" CTA renders even with zero
            links — it opens the drawer, which triggers the live retailer-link
            fetch that can recover links missing from the static snapshot. */}
        <div className='mt-auto flex flex-col'>
          {buyLinks.map((link) => (
            <RetailerRow
              key={link.url}
              label={link.label}
              url={link.url}
              price={link.price}
              currency={currency}
              logoUrl={link.logoUrl}
              verified={link.verified}
              onClick={
                reportAnalytics
                  ? () =>
                      reportAnalytics.onProductClick({
                        productSlug: slug ?? '',
                        productName: name,
                        outboundUrl: link.url,
                        price: link.price ?? null,
                        position: analyticsPosition ?? 0,
                        section: analyticsSection ?? 'ranked-product',
                      })
                  : undefined
              }
            />
          ))}

          {/* "See details" link */}
          {slug && (
            <div className='flex items-center justify-center pt-3'>
              {onViewProduct ? (
                <button
                  type='button'
                  onClick={() => onViewProduct(slug)}
                  // -my-2/py-2 widen the tap target to ≥24px (WCAG 2.5.8)
                  // without shifting layout
                  className='-my-2 py-2 text-sm font-semibold leading-5 text-info-700 hover:underline'
                >
                  See details
                </button>
              ) : (
                <a
                  href={`#${slug}`}
                  className='-my-2 py-2 text-sm font-semibold leading-5 text-info-700'
                >
                  See details
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Compact variant (for TopPicks cards)
// ---------------------------------------------------------------------------

function CompactProductCard({
  image,
  name,
  roleLabel,
  roleDescription,
  summary,
  buyLinks,
  currency,
  slug,
  index,
  isImageChecking = false,
  onViewProduct,
  reportAnalytics,
  analyticsSection,
  analyticsPosition,
}: {
  image: string | null
  name: string
  roleLabel?: string | null
  roleDescription?: string | null
  summary?: string | null
  buyLinks: BuyLink[]
  currency?: string | null
  slug?: string
  index?: number
  isImageChecking?: boolean
  onViewProduct?: (slug: string) => void
  reportAnalytics?: ReturnType<typeof useSeoReportAnalytics>
  analyticsSection?: string
  analyticsPosition?: number
}) {
  const isEager = index != null && index < 2

  return (
    <div className='flex h-full flex-col gap-3'>
      {/* Top content -- grows to fill available space */}
      <div className='flex flex-1 flex-col gap-3'>
        {roleLabel && (
          <span
            className={cn(
              'inline-flex w-fit items-center rounded-full px-3 py-0.5 text-xs font-medium leading-5 md:py-1 md:text-sm',
              getRoleBadgeClasses(roleLabel)
            )}
          >
            {roleLabel === 'Best Overall' ? 'Top Pick' : roleLabel}
          </span>
        )}

        {/* Product image */}
        <div className='relative flex aspect-square items-center justify-center overflow-hidden rounded-xl border-[0.5px] border-border bg-white'>
          {isImageChecking && <ImageCheckSkeletonOverlay />}
          {image ? (
            <ProductImage
              src={image}
              alt={name}
              width={288}
              height={288}
              className='h-full w-auto object-contain'
              priority={isEager}
            />
          ) : (
            <div className='flex h-full w-full items-center justify-center bg-gray-50 text-sm text-gray-400'>
              No image
            </div>
          )}
        </div>

        {/* Role description heading */}
        {/* TODO: roleDescription text is often too long for compact cards -- revisit how this data is represented */}
        {roleDescription && (
          <h3 className='line-clamp-2 text-balance text-xl font-semibold leading-[30px] text-foreground'>
            {roleDescription}
          </h3>
        )}

        {/* Product name */}
        <div className='flex flex-col gap-3 text-foreground-secondary'>
          {slug && onViewProduct ? (
            <button
              type='button'
              onClick={() => onViewProduct(slug)}
              className='text-balance text-left text-sm font-medium leading-6 underline md:text-base'
            >
              {name}
            </button>
          ) : slug ? (
            <a
              href={`#${slug}`}
              className='text-balance text-sm font-medium leading-6 underline md:text-base'
            >
              {name}
            </a>
          ) : (
            <span className='text-balance text-sm font-medium leading-6 md:text-base'>
              {name}
            </span>
          )}
        </div>

        {/* Summary */}
        {summary && (
          <p className='hidden line-clamp-4 text-sm font-normal leading-5 text-foreground-secondary text-ellipsis md:block'>
            {summary}
          </p>
        )}
      </div>

      {/* Retailer links -- pinned to bottom of card. "See details" renders
          even with zero links so the drawer (and its live link fetch) stays
          reachable when the static snapshot has none. */}
      <div className='mt-1 flex flex-col'>
        {buyLinks.map((link) => (
          <RetailerRow
            key={`${link.label}-${link.url}`}
            label={link.label}
            url={link.url}
            price={link.price}
            currency={currency}
            logoUrl={link.logoUrl}
            verified={link.verified}
            onClick={
              reportAnalytics
                ? () =>
                    reportAnalytics.onProductClick({
                      productSlug: slug ?? '',
                      productName: name,
                      outboundUrl: link.url,
                      price: link.price ?? null,
                      position: analyticsPosition ?? index ?? 0,
                      section: analyticsSection ?? 'top-picks',
                    })
                : undefined
            }
          />
        ))}
        {slug && onViewProduct ? (
          <div className='flex items-center justify-center pt-3'>
            <button
              type='button'
              onClick={() => onViewProduct(slug)}
              className='text-sm font-semibold leading-5 text-info-700 hover:underline'
            >
              See details
            </button>
          </div>
        ) : slug ? (
          <div className='flex items-center justify-center pt-3'>
            <a
              href={`#${slug}`}
              className='-my-2 py-2 text-sm font-semibold leading-5 text-info-700 hover:underline'
            >
              See details
            </a>
          </div>
        ) : null}
      </div>
    </div>
  )
}
